import { Box, Drawer, IconButton, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ArticleType, CheckoutSessionStatus } from '@nemuru-eng/js-api-client';

import { CartSummary } from '@/components/modules/_shared/CartSummary/CartSummary';
import { CartSummaryMap } from '@/components/modules/_shared/CartSummary/maps/CartSummary.map';
import { CustomerSummary } from '@/components/modules/_shared/CustomerSummary/CustomerSummary';
import { OrderSummary } from '@/components/modules/_shared/OrderSummary/OrderSummary';
import { ApplicationSummaryPaymentMethods } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/ApplicationSummaryPaymentMethods/ApplicationSummaryPaymentMethods';
import { CartAlerts } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/CartAlerts/CartAlerts';
import { DistributionAlert } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/DistributionAlert';
import { SelectedAgentChip } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/SelectedAgentChip';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { SectionTitle } from '@/components/modules/Application/components/_shared/components/Section/SectionTitle';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { ApplicationStep } from '@/components/modules/Application/types/ApplicationStep';
import { GenericApplicationStepValue } from '@/components/modules/Application/types/GenericApplicationStep';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Icon } from '@/components/shared/components/Icon';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { PendingDataType } from '@/config/types';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUser } from '@/hooks/useUser';

interface ApplicationSummaryProps {
  loading: boolean;
  checkoutSessionStatus?: CheckoutSessionStatus;
  forceShowSummaryOnDesktop?: boolean;
  showSectionTitle?: boolean;
}

export const ApplicationSummary = ({
  loading,
  checkoutSessionStatus,
  forceShowSummaryOnDesktop,
  showSectionTitle = true,
}: ApplicationSummaryProps) => {
  const { lg, md } = useScreen();
  const { getTextIdTranslation } = useTranslation();

  const {
    state: {
      cart,
      downPayment,
      orderReference1,
      orderReference2,
      hppId,
      cartSummaryData,
      customerData,
      showSummary,
      paymentMethodEntities,
      steps,
    },
  } = useApplicationContext();

  const {
    state: { showLeftMenu },
  } = usePageContext();

  const { step, showMobileSummary, toggleShowMobileSummary } = useApplicationNavigation();
  const { isUserAgent, isUserAdmin } = useUser();

  const mobileVersion: boolean = !(lg || (md && (!showLeftMenu || forceShowSummaryOnDesktop)));

  const showAgent: boolean = !isUserAgent || !!isUserAdmin;

  const showCartReference: boolean = !!orderReference1;

  const showCartSummary: boolean =
    !!cartSummaryData && (cartSummaryData.cartSummaryLines.length > 0 || !!cartSummaryData.total);

  const showPaymentMethods: boolean =
    !!paymentMethodEntities &&
    paymentMethodEntities.length > 0 &&
    (!step ||
      (
        [
          ApplicationStep.CONTACT_INFORMATION,
          ApplicationStep.CHECKOUT_SESSION_CREATION,
        ] as GenericApplicationStepValue[]
      ).includes(step));

  const showDetails: boolean =
    step !== ApplicationStep.AGENT_SELECTION &&
    step !== ApplicationStep.AUTO_CART &&
    (showCartSummary || showCartReference || showPaymentMethods);

  const showCartAlerts =
    !mobileVersion &&
    step &&
    (
      [
        ApplicationStep.DEFAULT_CART,
        ApplicationStep.AUTO_CART,
        ApplicationStep.KTUIN_CART,
      ] as GenericApplicationStepValue[]
    ).includes(step);

  const discount = cart?.totalDiscount();

  const customerSummaryData = customerData
    ? {
        fullName: {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
        },
        email: customerData.email ?? null,
        phoneNumber: customerData.phoneNumber ?? null,
        documentNumber: customerData.documentNumber ?? null,
      }
    : undefined;

  const isContactBeforePayment =
    steps.indexOf(ApplicationStep.CONTACT_INFORMATION) < steps.indexOf(ApplicationStep.PAYMENT_METHOD);

  const showCustomerSummary =
    !!customerSummaryData &&
    (step === ApplicationStep.CHECKOUT_SESSION_CREATION ||
      (isContactBeforePayment && step === ApplicationStep.PAYMENT_METHOD));

  const isAutoCart =
    (!!cartSummaryData && CartSummaryMap.isSingleAutomobileCart(cartSummaryData)) ||
    cart?.items[0]?.data.type === ArticleType.AUTOMOBILE ||
    steps.includes(ApplicationStep.AUTO_CART);

  const showOrderSummary = showCartReference && orderReference1 && !isAutoCart;

  const InnerComponent = (
    <Section>
      {showSectionTitle && (
        <SectionTitle
          title={'SUMMARY'}
          subtitle={'PAYMENT_PROPOSAL_DETAILS'}
          boxProps={{ style: { maxHeight: '60.5px', overflow: 'visible' } }}
        />
      )}
      {loading ? (
        <Skeleton variant="rect" width={'100%'} height={'600px'} />
      ) : (
        <Box display={'flex'} flexDirection={'column'} gridGap={16}>
          {showAgent && <SelectedAgentChip />}
          {showDetails && (
            <CustomPaper fill="paper">
              {showOrderSummary && (
                <OrderSummary
                  titleTextId="ORDER_DATA"
                  checkoutSessionStatus={checkoutSessionStatus}
                  orderReference1={orderReference1}
                  orderReference2={orderReference2}
                />
              )}
              {showCartSummary && cartSummaryData && (
                <>
                  {showOrderSummary && <CustomDivider margin={2} />}
                  <CartSummary
                    cartSummaryData={cartSummaryData}
                    downPayment={downPayment}
                    discount={discount}
                    checkoutSessionStatus={checkoutSessionStatus}
                    showAutoTitle={false}
                  />
                </>
              )}
              {showPaymentMethods && (
                <>
                  {showCartSummary && <CustomDivider margin={2} />}
                  <ApplicationSummaryPaymentMethods />
                </>
              )}
            </CustomPaper>
          )}
          {showCustomerSummary && (
            <CustomPaper fill="paper">
              <CustomerSummary customer={customerSummaryData} />
            </CustomPaper>
          )}
          {!showDetails && !showAgent && <PendingDataWarning type={PendingDataType.NO_DATA_TO_DISPLAY} />}
          {showCartAlerts && <CartAlerts />}
          {step && hppId && <DistributionAlert />}
          {mobileVersion && (
            <Box mt={2} textAlign={'end'}>
              <StandardButton variant="contained" color="secondary" onClick={toggleShowMobileSummary}>
                {getTextIdTranslation('CLOSE')}
              </StandardButton>
            </Box>
          )}
        </Box>
      )}
    </Section>
  );

  if (!mobileVersion) {
    return (
      <Box>
        <Box position="absolute" zIndex={10} top={0} right={0}>
          <CollapseIcon />
        </Box>
        <Box
          overflow="hidden"
          px="1px"
          pb="1px"
          style={{ opacity: showSummary ? 1 : 0, transition: 'opacity 0.6s ease-in-out' }}
        >
          <Box width={378}>{InnerComponent}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {(step === ApplicationStep.DEFAULT_CART || step === ApplicationStep.AUTO_CART) && (
        <Box display={'flex'} flexDirection={'column'} gridGap={12} mt={3}>
          <CartAlerts />
        </Box>
      )}
      <Drawer open={showMobileSummary} anchor={'top'} onClose={toggleShowMobileSummary}>
        <CustomPaper fill="paper" elevation={0} style={{ padding: '25px' }}>
          {InnerComponent}
        </CustomPaper>
      </Drawer>
    </>
  );
};

const CollapseIcon = () => {
  const { palette } = useCustomTheme();

  const {
    actions: { setShowSummary },
    state: { showSummary },
  } = useApplicationContext();

  const useStyles = makeStyles({
    nested: {
      transition: '0.1s ease',
      backgroundColor: palette.grey[50],
      color: palette.getContrastText(palette.grey[50]),
    },
    button: {
      '&:hover': {
        backgroundColor: palette.grey[100],
      },
    },
  });

  const classes = useStyles();

  return (
    <IconButton
      size="small"
      className={`${classes.nested} ${classes.button}`}
      onClick={() => setShowSummary(!showSummary)}
      style={{ border: `1px solid ${palette.grey[300]}` }}
    >
      <Icon
        style={{
          background: 'transparent',
          borderRadius: '50%',
          transform: showSummary ? 'rotateY(180deg)' : 'none',
          transition: 'transform 0.6s ease',
        }}
        size={20}
      >
        chevron_left
      </Icon>
    </IconButton>
  );
};
